import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { convertLocalToExternal } from '../services/imageService';

interface Teacher {
    id: number;
    name: string;
    subjects: { abbreviation: string; fullName: string }[];
    photoUrl: string;
    testimonial: string;
}

interface TeacherModalProps {
    teacher: Teacher | null;
    onClose: () => void;
}

const TeacherModal: React.FC<TeacherModalProps> = ({ teacher, onClose }) => {
    // Função para obter URL da imagem (converte path local para Cloudinary)
    const getPhotoUrl = (photoUrl: string): string => {
        return convertLocalToExternal(photoUrl);
    };

    return (
        <AnimatePresence>
            {teacher && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-black/90 backdrop-blur-md"
                            onClick={onClose}
                        />

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="relative w-full max-w-5xl flex flex-col md:flex-row rounded-3xl shadow-2xl overflow-hidden my-8"
                            style={{ 
                                background: 'linear-gradient(135deg, rgba(40, 40, 40, 0.98) 0%, rgba(20, 20, 20, 0.98) 100%)',
                                border: '4px solid #ffd700'
                            }}
                        >
                        {/* Botão Fechar */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-20 text-gold hover:text-white transition-colors h-12 w-12 flex items-center justify-center rounded-full bg-black/80 hover:bg-gold/30 border-2 border-gold shadow-lg"
                            aria-label="Fechar modal"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Foto do Professor - SEM SOMBRA ESCURA */}
                        <div className="w-full md:w-2/5 flex-shrink-0 relative bg-gradient-to-br from-gray-800 to-gray-900">
                            <img
                                src={getPhotoUrl(teacher.photoUrl)}
                                alt={teacher.name}
                                className="w-full h-56 sm:h-64 md:h-full object-contain"
                                style={{ filter: 'brightness(1.05)' }}
                            />
                        </div>

                        {/* Conteúdo - BEM MAIS CLARO */}
                        <div className="w-full md:w-3/5 flex flex-col p-6 sm:p-8 md:p-10" style={{ background: 'linear-gradient(135deg, rgba(50, 50, 50, 0.95) 0%, rgba(30, 30, 30, 0.95) 100%)' }}>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 tracking-wide font-playfair drop-shadow-lg" style={{ color: '#ffd700', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                                {teacher.name}
                            </h1>

                            {/* Badges das Matérias COM HOVER - BEM DESTACADAS */}
                            <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                                {teacher.subjects.map((subject, index) => (
                                    <div key={index} className="group relative inline-block">
                                        <span 
                                            className="inline-block px-3 py-2 sm:px-5 sm:py-3 text-xs sm:text-sm font-bold rounded-full shadow-lg transition-all duration-300 cursor-help hover:scale-105"
                                            style={{
                                                background: 'linear-gradient(135deg, #d4a020 0%, #c77408 100%)',
                                                color: '#000000',
                                                boxShadow: '0 4px 12px rgba(212, 160, 32, 0.5)',
                                                fontWeight: '700',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {subject.abbreviation}
                                        </span>
                                        {/* Tooltip no modal - CORRIGIDO PARA NÃO CORTAR */}
                                        <div 
                                            className="fixed opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border-2 z-[9999] shadow-2xl"
                                            style={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.98)',
                                                color: '#ffd700',
                                                fontSize: '0.875rem',
                                                fontWeight: 'bold',
                                                borderColor: '#ffd700',
                                                padding: '12px 18px',
                                                borderRadius: '12px',
                                                maxWidth: '350px',
                                                width: 'max-content',
                                                whiteSpace: 'normal',
                                                lineHeight: '1.5',
                                                textAlign: 'center',
                                                top: 'auto',
                                                bottom: 'auto',
                                                left: '50%',
                                                transform: 'translateX(-50%) translateY(-120%)',
                                                marginTop: '-10px'
                                            }}
                                        >
                                            {subject.fullName}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Divisor */}
                            <div className="w-24 h-1 mb-4 sm:mb-6 rounded-full shadow-lg" style={{ background: 'linear-gradient(90deg, #ffd700 0%, #ffed4e 50%, transparent 100%)' }} />

                            {/* Depoimento - TEXTO SUPER CLARO E LEGÍVEL */}
                            <div className="relative p-4 sm:p-6 rounded-r-2xl shadow-lg" style={{ 
                                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.20) 0%, rgba(255, 193, 7, 0.12) 100%)',
                                borderLeft: '4px solid #ffd700'
                            }}>
                                <svg
                                    className="absolute top-2 left-2 w-6 h-6 sm:w-8 sm:h-8"
                                    fill="currentColor"
                                    viewBox="0 0 32 32"
                                    style={{ color: 'rgba(255, 215, 0, 0.4)' }}
                                >
                                    <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2h2V8h-2zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2h2V8h-2z" />
                                </svg>
                                <p className="text-sm sm:text-base lg:text-lg leading-relaxed font-poppins pl-4 sm:pl-6" style={{ color: '#FFFFFF', fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    {teacher.testimonial}
                                </p>
                                <svg
                                    className="absolute bottom-2 right-2 w-6 h-6 sm:w-8 sm:h-8 transform rotate-180"
                                    fill="currentColor"
                                    viewBox="0 0 32 32"
                                    style={{ color: 'rgba(255, 215, 0, 0.4)' }}
                                >
                                    <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2h2V8h-2zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2h2V8h-2z" />
                                </svg>
                            </div>

                            {/* Rodapé decorativo */}
                            <div className="mt-4 sm:mt-6 flex items-center gap-2 text-xs sm:text-sm font-medium" style={{ color: '#ffd700' }}>
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                </svg>
                                <span>Professor(a) • Anuário AMS 2025</span>
                            </div>
                        </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default TeacherModal;
